import axios from "axios";
import moment from "moment";
import React, { createContext, useEffect, useState } from "react";
import api from "services/api";
import { storeTokens, clearTokens, getStoredAdmin, getAccessToken, getLegacyToken } from "utils/tokenStorage";

export const AuthContext = createContext();

export const withAuthContext = (Component) => (props) =>
	<AuthContext.Consumer>{(value) => <Component {...value} {...props} />}</AuthContext.Consumer>;

const AuthProvider = ({ children }) => {
	// Token state supports new accessToken and legacy token for backward compatibility
	const [Token, setToken] = useState(""); // will hold accessToken primarily
	const [refreshToken, setRefreshToken] = useState("");
	const [currAdmin, setcurrAdmin] = useState({});
	const [AdminRole, setAdminRole] = useState("");
	const [loading, setLoading] = useState(true);
	const [isAuthenticated, setIsAuthenticated] = useState(false);

	const [DashboardData, setDashboardData] = useState({
		Tasks: [],
		Forwarded: [],
		Pending: [],
		Completed: [],
		InProgress: [],
		Services: [],
		Complain: []
	});

	const [From, setFrom] = useState("");
	const [To, setTo] = useState("");

	const GetCurrentAdmin = () => {
		const activeToken = Token || getAccessToken() || getLegacyToken();
		if (activeToken) {
			api
				.get(`/AdminInfo`)
				.then((res) => {
					if (res?.data?.status === 200) {
						setcurrAdmin(res?.data?.data);
						setAdminRole(res?.data?.data?.Role);
						localStorage.setItem('admin', JSON.stringify(res?.data?.data));
						setIsAuthenticated(true);
					}
				})
				.catch(() => {
					setIsAuthenticated(false);
				});
		} else {
			setIsAuthenticated(false);
		}
	};

	const GetDashboard = () => {
		const activeToken = Token || getAccessToken() || getLegacyToken();
		if (activeToken) {
			api
				.get(`/DashboardAdmin/${From}/${To}`)
				.then((res) => {
					if (res?.data?.status === 200) {
						setDashboardData(res?.data?.data);
					}
				})
				.catch(() => {});
		}
	};

	function CheckToken() {
		// Prefer new accessToken
		const access = getAccessToken();
		const legacy = getLegacyToken();
		if (access) {
			setToken(access);
			setIsAuthenticated(true);
		} else if (legacy) {
			setToken(legacy);
			setIsAuthenticated(true);
		} else {
			setToken("");
			setIsAuthenticated(false);
		}
	}

	// Admin login using new token response format
	const loginAdmin = async (email, password) => {
		try {
			const res = await api.post('/Login-Admin', { email, password });
			const { accessToken, refreshToken: rToken, token: legacyToken, status, message } = res.data;
			if (status === 200) {
				storeTokens(accessToken, rToken, legacyToken, null, true);
				setToken(accessToken || legacyToken || "");
				setRefreshToken(rToken || "");
				CheckToken();
				await GetCurrentAdmin();
				return { success: true, message };
			}
			return { success: false, message: message || 'Login failed' };
		} catch (err) {
			if (err?.response?.status === 429) {
				return { success: false, message: 'Too many login attempts. Please try again later.' };
			}
			const msg = err?.response?.data?.message || 'Login failed';
			return { success: false, message: msg };
		}
	};

	const logout = async () => {
		try {
			await api.post('/auth/logout');
		} catch (_) {}
		finally {
			clearTokens();
			setToken("");
			setRefreshToken("");
			setcurrAdmin({});
			setIsAuthenticated(false);
		}
	};

	useEffect(() => {
		CheckToken();
		const storedAdmin = getStoredAdmin();
		if (storedAdmin) {
			setcurrAdmin(storedAdmin);
			setAdminRole(storedAdmin?.Role);
			setIsAuthenticated(true);
		}
		setLoading(false);
	}, []);

	useEffect(() => {
		setFrom(moment().subtract(1, "year").format("YYYY-MM-DD"));
		setTo(moment().format("YYYY-MM-DD"));
	}, []);

	useEffect(() => {
		if (Token) {
			GetCurrentAdmin();
		}
	}, [Token]);

	useEffect(() => {
		GetDashboard();
	}, [From, To]);

	return (
		<AuthContext.Provider
			value={{
				Token,
				refreshToken,
				isAuthenticated,
				loading,
				loginAdmin,
				logout,
				CheckToken,
				currAdmin,
				GetCurrentAdmin,
				AdminRole,
				DashboardData,
				GetDashboard
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export default AuthProvider;
